import {
  SpecificCause,
  TreeGeneralCause,
  TreeLoss
} from '_clean-code/types/stops';
import { StopAutomationRecord } from '_clean-code/api/hooks/useStopAutomations';

import { I18n } from '@utils/i18n';

const reA = /[^a-zA-Z]/g;
const reN = /[^0-9]/g;

const sorter = {
  morning: 1,
  afternoon: 2,
  evening: 3,
  night: 4,
  day: 5
};

const sortTimeStrings = (
  data: { id: number; label: string; value: string }[]
): { id: number; label: string; value: string }[] =>
  data.sort((a, b) => {
    const day1 = a.value.toLowerCase();
    const day2 = b.value.toLowerCase();
    return sorter[day1] - sorter[day2];
  });

const sortAlphabetically = (
  list: {
    value: string;
    label: string;
    id: number;
  }[]
): {
  value: string;
  label: string;
  id: number;
}[] =>
  list.sort((a, b) => {
    const aA = a.label.replace(reA, '');
    const bA = b.label.replace(reA, '');
    if (aA === bA) {
      const aN = parseInt(a.label.replace(reN, ''), 10);
      const bN = parseInt(b.label.replace(reN, ''), 10);
      // eslint-disable-next-line no-nested-ternary
      return aN === bN ? 0 : aN > bN ? 1 : -1;
    }
    return aA > bA ? 1 : -1;
  });

export function getAllMachineOptions(stops: any[]) {
  const uniqueMachines = new Set(stops?.map<string>((e) => e?.machine || ''));
  const mappedMachines = Array.from(uniqueMachines).map((e, index) => ({
    id: index + 1,
    label: e,
    value: e
  }));
  const sortedMachines = sortAlphabetically(mappedMachines);
  return sortedMachines;
}
export function getAllShiftTitleOptions(stops: any[]) {
  const uniqueShiftTitles = new Set(
    stops?.map<string>((e) => e?.shiftTitle || '')
  );
  const mappedShiftTitles = Array.from(uniqueShiftTitles).map((e, index) => ({
    id: index + 1,
    label: I18n.t(e),
    value: e
  }));
  return sortTimeStrings(mappedShiftTitles);
}

export function getAllGeneralCauseOptions(stops: any[]) {
  const uniqueGeneralCauses = new Set(
    stops?.map<string>((e) => e?.classification?.generalCause || '')
  );
  const mappedGeneralCauses = Array.from(uniqueGeneralCauses).map(
    (e, index) => ({
      id: index + 1,
      label: e,
      value: e
    })
  );
  const sortedGeneralCauses = sortAlphabetically(mappedGeneralCauses);
  return sortedGeneralCauses;
}

export function getAllSpecificCauseOptions(stops: any[]) {
  const uniqueSpecificCauses = new Set(
    stops?.map<string>((e) => e?.classification?.specificCause || '')
  );
  const mappedSpecificCauses = Array.from(uniqueSpecificCauses).map(
    (e, index) => ({
      id: index + 1,
      label: e,
      value: e
    })
  );
  const sortedSpecificCauses = sortAlphabetically(mappedSpecificCauses);
  return sortedSpecificCauses;
}

/**
 * Returns all the available general and specific causes a user can select to filter the
 * stops table. Only general/specific causes that are assigned to at least 1 machine are considered.
 * Automation-assigned causes (e.g. microstops) are often kept out of loss trees so operators can't
 * pick them manually, yet stops still carry them, so they must be filterable too.
 */
export const getGeneralAndSpecificCausesOptions = (
  treeLosses: TreeLoss[],
  stopAutomations: StopAutomationRecord[] = []
) => {
  const unclassifiedOption = {
    label: I18n.t('unclassified'),
    value: 'UNCLASSIFIED'
  };
  if (!treeLosses) {
    return {
      generalCausesOptions: [unclassifiedOption],
      specificCausesOptions: [unclassifiedOption]
    };
  }
  const generalCausesMap = new Map<string, TreeGeneralCause>();
  const specificCausesMap = new Map<string, SpecificCause>();
  treeLosses
    .filter((tl) => (tl.machines?.length ?? 0) > 0)
    .forEach((tl) => {
      tl.generalCauses.forEach((gc) => {
        generalCausesMap.set(gc.id ?? gc.name, gc);
        gc.specificCauses.forEach((sc) => {
          specificCausesMap.set(sc.id ?? sc.name, sc);
        });
      });
    });
  stopAutomations.forEach(({ stopLabels }) => {
    if (
      stopLabels.generalCause &&
      !generalCausesMap.has(stopLabels.generalCause.id)
    ) {
      generalCausesMap.set(stopLabels.generalCause.id, {
        ...stopLabels.generalCause,
        specificCauses: []
      });
    }
    if (
      stopLabels.specificCause &&
      !specificCausesMap.has(stopLabels.specificCause.id)
    ) {
      specificCausesMap.set(
        stopLabels.specificCause.id,
        stopLabels.specificCause
      );
    }
  });
  const generalCausesWithMachines = Array.from(generalCausesMap.values()).map(
    (gc) => ({
      label: gc.name,
      value: gc.id
    })
  );
  const specificCausesWithMachines = Array.from(specificCausesMap.values()).map(
    (sc) => ({
      label: sc.name,
      value: sc.id
    })
  );
  const sortedGeneralCauses = generalCausesWithMachines.sort((a, b) =>
    a.label.localeCompare(b.label)
  );
  const sortedSpecificCauses = specificCausesWithMachines.sort((a, b) =>
    a.label.localeCompare(b.label)
  );
  return {
    generalCausesOptions: [unclassifiedOption, ...sortedGeneralCauses],
    specificCausesOptions: [unclassifiedOption, ...sortedSpecificCauses]
  };
};

export const UNCLASSIFIED_CAUSE_VALUE = 'UNCLASSIFIED';

/**
 * The stops endpoint ORs `include` with `unclassified`, so "these causes or unclassified" sends
 * both. Selecting every option is equivalent to not filtering, so the filter is omitted.
 */
export const buildCauseFilter = (
  selected: { value?: string | number }[],
  allOptions: { value?: string | number }[]
): { include?: string[]; unclassified?: true } | undefined => {
  if (selected.length === 0) return undefined;
  const selectedValues = new Set(selected.map((x) => x.value));
  if (allOptions.every((x) => selectedValues.has(x.value))) return undefined;
  const include = selected
    .filter((x) => x.value !== UNCLASSIFIED_CAUSE_VALUE)
    .map((x) => x.value as string);
  return {
    ...(include.length > 0 && { include }),
    ...(selectedValues.has(UNCLASSIFIED_CAUSE_VALUE) && { unclassified: true })
  };
};

export function getAllGeneralCausesForAMachineOptions(
  treeLosses: TreeLoss[],
  machineId: number
) {
  const index = treeLosses?.findIndex(
    (treeLoss) =>
      treeLoss.machines?.findIndex(
        (machine) => machine.machineId === machineId
      ) !== -1
  );
  const treeLoss = index !== -1 ? treeLosses[index] : null;
  const result = treeLoss
    ? treeLoss.generalCauses
        .sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        )
        .map((x) => ({ label: x.name, value: x.id! }))
    : [];
  return [{ label: '‍', value: '' }, ...result];
}

export function getAllSpecificCausesForAMachineOptions(
  treeLosses: TreeLoss[],
  machineId: number,
  generalCauseId: string | null
) {
  const treeLoss = treeLosses?.find(
    (e) =>
      e.machines?.findIndex((machine) => machine.machineId === machineId) !== -1
  );
  if (!treeLoss) return [];
  const generalCause = treeLoss.generalCauses.find(
    (e) => e.id === generalCauseId
  );
  if (!generalCause) return [{ label: '‍', value: '' }];
  const result = generalCause.specificCauses
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    .map((x) => ({ label: x.name, value: x.id! }));
  return [{ label: '‍', value: '' }, ...result];
}

export function getAllComponentForAMachineOptions(
  components: any[],
  machineId: number
) {
  const seen = new Set<string>();
  const result = components
    ?.filter((x) => x.machineId === machineId)
    .filter((x) => {
      if (seen.has(x.componentId)) return false;
      seen.add(x.componentId);
      return true;
    })
    .sort((a, b) =>
      a.componentName.toLowerCase().localeCompare(b.componentName.toLowerCase())
    )
    .map((x) => ({ label: x.componentName, value: x.componentId }));
  return [{ label: '‍', value: '' }, ...result];
}

export function mapMachinesAndTreelosses(treeLosses: TreeLoss[]) {
  const data: Record<string, number[]> = {};
  treeLosses.forEach((tl) => {
    data[tl.id] = (tl.machines ?? []).map((m) => m.machineId);
  });
  return data;
}
